import accountApi from '../store/apis/account-api';
import customerNetApi from '../store/apis/cnet-api';
import productApi from '../store/apis/product-api';
import userApi from '../store/apis/user-api';
import { setActivePropertyId, setUser } from '../store/slices/auth-slice';
import store from '../store/store';
import hydrateUserContext from '../store/thunks/user-context-thunk';
import type { AddressListItem, UserSessionData } from '../types/api-types';
import { getSalesAgreement } from './core-utils';

export const isOktaAdminEmployeeUser = (user?: UserSessionData | null) =>
  !!(user?.isOktaLogin && user.typeOfUser === 'employee');

const getOktaAdminShipToAddress = (
  addresses?: AddressListItem[],
  activePropertyId?: string | null
) => {
  if (!addresses?.length) {
    return null;
  }

  return (
    addresses.find(
      address =>
        !!activePropertyId &&
        address.SITE_USE_ID.toString() === activePropertyId
    ) ||
    addresses.find(
      address =>
        address.PURPOSE?.toUpperCase().includes('SHIP') &&
        address.PURPOSE_STATUS?.toUpperCase() !== 'I'
    ) ||
    addresses.find(address =>
      address.PURPOSE?.toUpperCase().includes('SHIP')
    ) ||
    addresses[0]
  );
};

const hydrateOktaAdminSession = async (user: UserSessionData) => {
  const menuPromise = store
    .dispatch(productApi.endpoints.menu.initiate())
    .unwrap();
  const selectedAccountNumber =
    user.selectedAccountNumber || user.punchOutAccNumber?.toString();
  let nextUser = user;

  if (selectedAccountNumber) {
    const accountDetails = await store
      .dispatch(
        customerNetApi.springBoot.endpoints.accountDetails.initiate({
          accountNumber: selectedAccountNumber,
          ssoId: user.sso,
        })
      )
      .unwrap()
      .then(response => response.responseObject?.[0]);

    if (accountDetails?.PARTYID) {
      const addresses = await store
        .dispatch(
          customerNetApi.springBoot.endpoints.addressList.initiate({
            accountNumber: selectedAccountNumber,
            partyId: accountDetails.PARTYID,
          })
        )
        .unwrap()
        .then(response => response.responseObject);

      const selectedProperty = getOktaAdminShipToAddress(
        addresses,
        user.activePropertyId
      );

      if (selectedProperty) {
        nextUser = {
          ...user,
          selectedAccountNumber,
          activePropertyId: selectedProperty.SITE_USE_ID.toString(),
        };

        const salesAgreementsPromise = store.dispatch(
          customerNetApi.springBoot.endpoints.salesAgreements.initiate({
            accountNumber: selectedAccountNumber,
          })
        );

        void salesAgreementsPromise.then(() => {
          const standardSalesAgreement =
            getSalesAgreement('Standard Agreement');
          const partsSalesAgreement = getSalesAgreement('GEA Parts');

          void store.dispatch(
            productApi.endpoints.orderableMenu.initiate({
              accountNumber: standardSalesAgreement.ACCOUNT_NUMBER,
              agreementNumber:
                standardSalesAgreement.AGREEMENT_NUMBER.toString(),
            })
          );

          if (partsSalesAgreement) {
            void store.dispatch(
              productApi.endpoints.orderableMenu.initiate({
                accountNumber: partsSalesAgreement.ACCOUNT_NUMBER,
                agreementNumber:
                  partsSalesAgreement.AGREEMENT_NUMBER.toString(),
              })
            );
          }
        });

        await Promise.all([
          salesAgreementsPromise,
          store.dispatch(
            customerNetApi.springBoot.endpoints.shippingLocations.initiate({
              accountNumber: selectedAccountNumber,
              state: selectedProperty.STATE,
              countryCode: selectedProperty.COUNTRY,
              postalCode: selectedProperty.POSTALCODE.split('-')[0],
            })
          ),
          store.dispatch(
            productApi.endpoints.cart.initiate({
              accountNumber: selectedAccountNumber,
            })
          ),
        ]);
      }
    }
  }

  await menuPromise;

  if (nextUser !== user) {
    store.dispatch(setUser(nextUser));
  } else if (!store.getState().auth.isInitialized) {
    store.dispatch(setUser(user));
  }

  return { isAuthenticated: true };
};

export const prepareSession = async () => {
  const { user, isInitialized } = store.getState().auth;
  if (user) {
    void store.dispatch(
      customerNetApi.partsSpringBoot.endpoints.priceSubTypes.initiate()
    );

    if (isOktaAdminEmployeeUser(user)) {
      return hydrateOktaAdminSession(user);
    }

    if (!isInitialized) {
      const userDetails = await store
        .dispatch(
          customerNetApi.user.endpoints.userDetails.initiate({
            userId: user.sso,
          })
        )
        .unwrap()
        .then(response => response.responseObject?.[0]);

      if (userDetails) {
        const menuPromise = store
          .dispatch(productApi.endpoints.menu.initiate())
          .unwrap();
        const localUserDetailsPromise = store.dispatch(
          customerNetApi.user.endpoints.localUserDetails.initiate({
            personPartyId: userDetails.PERSON_PARTY_ID,
            userId: user.sso,
          })
        );

        const favoritesPromise = store
          .dispatch(
            productApi.endpoints.favorites.initiate({
              userId: user.sso,
            })
          )
          .unwrap()
          .then(favorites => {
            const FAVORITE_LISTS = ['My Favorites', 'My Favorite Packages'];

            const listsToCreate: string[] = [];

            FAVORITE_LISTS.forEach(favoriteListName => {
              if (
                !Object.values(favorites).some(
                  favoriteListDetails =>
                    favoriteListDetails.name.toLowerCase() ===
                    favoriteListName.toLowerCase()
                )
              ) {
                listsToCreate.push(favoriteListName);
              }
            });

            if (listsToCreate.length) {
              return store.dispatch(
                productApi.endpoints.upsertFavoriteItems.initiate({
                  body: {
                    items: listsToCreate.map(favoriteListName => ({
                      accountNumber: `favorites#${user.sso.toLowerCase()}`,
                      listName: favoriteListName,
                      item: null,
                      imagePath: '',
                      quantity: 0,
                      userFullName: `${userDetails.PERSON_FIRST_NAME} ${userDetails.PERSON_LAST_NAME}`,
                      userId: user.sso.toLowerCase(),
                    })),
                  },
                })
              );
            }
          });

        const propertyGroupsPromise = store.dispatch(
          userApi.endpoints.propertyGroups.initiate({ userId: user.sso })
        );

        const addressesByPartyId = await store
          .dispatch(
            accountApi.endpoints.addressesByPartyId.initiate({
              partyId: userDetails.PERSON_PARTY_ID,
            })
          )
          .unwrap()
          .then(
            addressesByPartyIdResponse =>
              addressesByPartyIdResponse.responseObject
          );

        const [, , propertyGroupsResponse] = await Promise.all([
          menuPromise,
          localUserDetailsPromise,
          propertyGroupsPromise.unwrap(),
          favoritesPromise,
        ]);

        let activePropertyId = store.getState().auth.user?.activePropertyId;

        if (!activePropertyId) {
          const lastUsedProperty = propertyGroupsResponse.responseObject?.find(
            propertyGroup => propertyGroup.lastUsedAddress
          );
          let activeProperty =
            lastUsedProperty &&
            addressesByPartyId.find(
              addressByPartyId =>
                addressByPartyId.SITE_USE_ID.toString() ===
                lastUsedProperty.siteUsedId
            );

          if (!activeProperty) {
            activeProperty = addressesByPartyId.find(
              addressByPartyId => addressByPartyId.SITE_USE_CODE === 'SHIP_TO'
            );
          }

          if (activeProperty) {
            activePropertyId = activeProperty.SITE_USE_ID.toString();
            store.dispatch(setActivePropertyId(activePropertyId));
          }
        }

        await store.dispatch(
          hydrateUserContext({ propertyId: activePropertyId! })
        );
      }
    } else {
      await store.dispatch(
        hydrateUserContext({ propertyId: user.activePropertyId! })
      );
    }
  }
  return { isAuthenticated: !!user };
};
